import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityGithubPullRequestsOverviewCard,
  EntityGithubPullRequestsTable,
  isGithubPullRequestsAvailable,
} from '@roadiehq/backstage-plugin-github-pull-requests';
import { BackstageGuard, GitHubGuard } from '@mercedes-benz/permission';
import { ownershipPermission } from '@mercedes-benz/permission-common';
import Grid from '@mui/material/Grid';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography } from '@mercedes-benz/odp-theme-v2';

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    cardWrapper: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      overflow: 'hidden',
    },
    emptyState: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      padding: 48,
      minHeight: 400,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      textAlign: 'center',
    },
    iconWrapper: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      background: 'rgba(0, 120, 214, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        width: 28,
        height: 28,
        color: c.primary.main,
      },
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: c.text.primary,
      fontWeight: 600,
      fontSize: '1.1rem',
      margin: 0,
      userSelect: 'none',
    },
    description: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: fgAlpha(0.55),
      fontSize: '0.875rem',
      margin: 0,
      maxWidth: 400,
      lineHeight: 1.6,
    },
  };
});

export const PullRequestsTab = () => {
  const { classes } = useStyles();
  const { entity } = useEntity();

  if (!isGithubPullRequestsAvailable(entity)) {
    return (
      <div className={classes.emptyState}>
        <div className={classes.iconWrapper}>
          <MergeTypeIcon />
        </div>
        <h3 className={classes.title}>No GitHub repository configured</h3>
        <p className={classes.description}>
          Add a <code>github.com/project-slug</code> annotation to enable pull
          request tracking for this component.
        </p>
      </div>
    );
  }

  return (
    <GitHubGuard feature="Pull Requests">
      <BackstageGuard permission={ownershipPermission}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <div className={classes.cardWrapper}>
              <EntityGithubPullRequestsOverviewCard />
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.cardWrapper}>
              <EntityGithubPullRequestsTable />
            </div>
          </Grid>
        </Grid>
      </BackstageGuard>
    </GitHubGuard>
  );
};
